import React from 'react';
import { Box, Typography, IconButton } from "@mui/material";
import { PersonalityRate } from "../../types/damage";
import { calculateActualStats } from "../../lib/stat";
import { StatInputs } from "../../types/stat";
import { PokemonDefenceInterface } from "../../pokemon/pokemon";
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { MAX_TOTAL_EFFORT } from '../../constants/effort';

interface StatsDisplayHeaderProps {
  selectedPokemon: PokemonDefenceInterface;
  localStatInputs: StatInputs;
  localTotalEvs: number;
  isStatsExpanded: boolean;
  setIsStatsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

function StatsDisplayHeader({
  selectedPokemon,
  localStatInputs,
  localTotalEvs,
  isStatsExpanded,
  setIsStatsExpanded,
  setModalOpen
}: StatsDisplayHeaderProps) {
  // Actual Stats Display (ローカル状態を使用)
  const actualStats = React.useMemo(() => {
    return calculateActualStats(selectedPokemon, localStatInputs);
  }, [selectedPokemon, localStatInputs]);

  // Helper function to format stat with EV and nature modifier
  const formatStat = (value: number, effortValue: number, personality: PersonalityRate) => {
    const personalitySymbol = personality === 1.1 ? '+' : personality === 0.9 ? '-' : '';
    return `${value}(${effortValue}${personalitySymbol})`;
  };

  const handleToggleStats = () => {
    setIsStatsExpanded(prev => !prev);
  };

  const handleToggleStatsByKey: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggleStats();
    }
  };

  return (
    <>
      {/* Actual Stats Display */}
      <Box sx={{ mt: 2 }}>
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'monospace',
            color: localTotalEvs > MAX_TOTAL_EFFORT ? 'error.main' : 'primary.main',
            fontSize: '0.8rem',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {formatStat(actualStats.hp, localStatInputs.hp_effort, 1.0)}-{formatStat(actualStats.attack, localStatInputs.attack_effort, localStatInputs.attack_personality)}-{formatStat(actualStats.defense, localStatInputs.defense_effort, localStatInputs.defense_personality)}-{formatStat(actualStats.specialAttack, localStatInputs.special_attack_effort, localStatInputs.special_attack_personality)}-{formatStat(actualStats.specialDefense, localStatInputs.special_defense_effort, localStatInputs.special_defense_personality)}-{formatStat(actualStats.speed, localStatInputs.speed_effort, localStatInputs.speed_personality)}
        </Typography>
      </Box>

      {/* Stats and EV Settings */}
      <Box sx={{ mt: 3 }}>
        {/* ヘッダー全体をクリック可能にして開閉しやすくする */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            mb: 2,
            cursor: 'pointer',
            userSelect: 'none',
          }}
          role="button"
          tabIndex={0}
          onClick={handleToggleStats}
          onKeyDown={handleToggleStatsByKey}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">
              能力設定
            </Typography>
            {/* 66を超える場合のアラート表示 */}
            {localTotalEvs > MAX_TOTAL_EFFORT && (
              <Typography 
                variant="body2" 
                color="error"
                sx={{ 
                  fontWeight: 'bold',
                  backgroundColor: 'error.light',
                  color: 'error.contrastText',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.75rem'
                }}
              >
                努力値超過
              </Typography>
            )}
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                setModalOpen(true);
              }}
              disabled={localTotalEvs > MAX_TOTAL_EFFORT}
              sx={{
                color: localTotalEvs > MAX_TOTAL_EFFORT ? 'text.disabled' : 'primary.main',
                border: '1px solid',
                borderColor: localTotalEvs > MAX_TOTAL_EFFORT ? 'text.disabled' : 'primary.main',
                borderRadius: 1,
                padding: '4px',
                '&:hover': {
                  backgroundColor: localTotalEvs > MAX_TOTAL_EFFORT ? 'transparent' : 'primary.light',
                  color: localTotalEvs > MAX_TOTAL_EFFORT ? 'text.disabled' : 'white'
                }
              }}
            >
              <TextSnippetIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography 
              variant="body2" 
              color={localTotalEvs > MAX_TOTAL_EFFORT ? "error" : "text.secondary"}
              sx={{ fontWeight: localTotalEvs > MAX_TOTAL_EFFORT ? 'bold' : 'normal' }}
            >
              合計: {localTotalEvs}/{MAX_TOTAL_EFFORT}
            </Typography>
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                handleToggleStats();
              }}
              sx={{
                color: 'text.secondary',
                padding: '2px',
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              {isStatsExpanded ? <ExpandLessIcon sx={{ fontSize: 20 }} /> : <ExpandMoreIcon sx={{ fontSize: 20 }} />}
            </IconButton>
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default StatsDisplayHeader;
